import React, {useState} from 'react';
import {Dialog} from 'primereact/dialog';
import {InputText} from 'primereact/inputtext';
import {Message} from 'primereact/message';
import Button from '@/core/components/button/Button';
import {AuthService} from '../service/authService';
import {API_ENDPOINTS} from '@/core/constants/endpoints';
import {apiService} from '@/core/api';
import './ForgotPasswordModal.scss';

interface ForgotPasswordModalProps {
  visible: boolean;
  onHide: () => void;
}

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({visible, onHide}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiService.post(API_ENDPOINTS.SIGNUP.REQUEST_PASSWORD_RESET, {email});
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError(null);
    setSuccess(false);
    onHide();
  };

  const footer = success ? (
    <div className="forgot-password-footer">
      <Button variant="primary" size="large" onClick={handleClose} className="w-full">
        Close
      </Button>
    </div>
  ) : (
    <div className="forgot-password-footer">
      <Button variant="secondary" size="large" onClick={handleClose} outlined className="cancel-btn">
        Cancel
      </Button>
      <Button
        variant="primary"
        size="large"
        onClick={handleSubmit}
        loading={loading}
        disabled={loading}
        className="submit-btn"
      >
        Reset
      </Button>
    </div>
  );

  return (
    <Dialog
      header="Reset Password"
      visible={visible}
      onHide={handleClose}
      modal
      className="forgot-password-modal"
      style={{width: '450px'}}
      footer={footer}
    >
      {success ? (
        <div className="success-content">
          <Message
            severity="success"
            text="Password reset instructions have been sent to your email address."
            className="w-full mb-3"
          />
          <p className="text-center">Please check your email and follow the instructions to reset your password.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="forgot-password-form">
          <p className="mb-4">Enter your email address and we'll send you instructions to reset your password.</p>

          <div className="form-group">
            <label htmlFor="email" className="block mb-2">
              Email Address
            </label>
            <InputText
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className={`w-full ${error ? 'p-invalid' : ''}`}
              autoFocus
            />
            {error && <small className="p-error mt-1">{error}</small>}
          </div>
        </form>
      )}
    </Dialog>
  );
};
